import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { rutaPrivacidad } from "@/lib/legalPaths";
import {
  abrirAjustesCookies,
  alAbrirAjustesCookies,
  alCambiarConsentimiento,
  guardarConsentimiento,
  leerConsentimiento,
  VIGENCIA_MESES,
  type Eleccion,
} from "@/lib/cookieConsent";

interface Textos {
  titulo: string;
  texto: string;
  mas: string;
  menos: string;
  detalles: string[];
  google: string;
  politica: string;
  rechazar: string;
  aceptar: string;
  cerrar: string;
  actual: Record<Eleccion, string>;
}

/** Enlace del pie para volver a abrir el banner (el texto del banner lo cita tal cual) */
const AJUSTES_COOKIES: Record<string, string> = {
  es: "Configurar cookies",
  en: "Cookie settings",
  pt: "Definições de cookies",
  fr: "Paramètres des cookies",
};

const TEXTOS_COOKIES: Record<string, Textos> = {
  es: {
    titulo: "Cookies",
    texto: `Con tu permiso, usamos cookies propias y de Google para saber qué anuncios de Google Ads nos traen solicitudes de visita. Si las rechazas, la web funciona igual. Puedes cambiar tu elección cuando quieras en «${AJUSTES_COOKIES.es}», al pie de la página.`,
    mas: "Configurar y más información",
    menos: "Ocultar detalles",
    detalles: [
      "Responsable: Noguera y Valdés SLU (Gándara International School), info@gandaraschool.com.",
      "Publicidad, solo si aceptas: Google Ads (Google Ireland Ltd.) guarda cookies _gcl_ (como _gcl_au y _gcl_aw) en este dominio durante 90 días, y otras en dominios de Google, para relacionar el clic en un anuncio con la solicitud de visita. No se usan para mostrarte publicidad personalizada. Google puede tratar datos en EE. UU. con las garantías del Marco de Privacidad de Datos UE-EE. UU.",
      `Técnica, siempre: tu elección se guarda en este navegador durante ${VIGENCIA_MESES} meses para no volver a preguntarte.`,
      "Puedes ejercer tus derechos sobre tus datos escribiendo a info@gandaraschool.com y reclamar ante la Agencia Española de Protección de Datos (aepd.es).",
    ],
    google: "Cómo usa Google estos datos",
    politica: "Política de privacidad",
    rechazar: "Rechazar",
    aceptar: "Aceptar",
    cerrar: "Cerrar",
    actual: { aceptadas: "Ahora mismo las tienes aceptadas.", rechazadas: "Ahora mismo las tienes rechazadas." },
  },
  en: {
    titulo: "Cookies",
    texto: `With your permission, we use our own and Google's cookies to see which of our Google ads bring us visit requests. If you reject them, the site works just the same. You can change your choice at any time under “${AJUSTES_COOKIES.en}” at the bottom of the page.`,
    mas: "Settings and more information",
    menos: "Hide details",
    detalles: [
      "Controller: Noguera y Valdés SLU (Gándara International School), info@gandaraschool.com.",
      "Advertising, only if you accept: Google Ads (Google Ireland Ltd.) stores _gcl_ cookies (such as _gcl_au and _gcl_aw) on this domain for 90 days, and others on Google domains, to link an ad click with a visit request. They are not used to show you personalised ads. Google may process data in the US under the EU-US Data Privacy Framework.",
      `Technical, always: your choice is stored in this browser for ${VIGENCIA_MESES} months so we don't ask again.`,
      "You can exercise your data rights by writing to info@gandaraschool.com and complain to the Spanish Data Protection Agency (aepd.es).",
    ],
    google: "How Google uses this data",
    politica: "Privacy policy",
    rechazar: "Reject",
    aceptar: "Accept",
    cerrar: "Close",
    actual: { aceptadas: "You currently accept them.", rechazadas: "You currently reject them." },
  },
  pt: {
    titulo: "Cookies",
    texto: `Com a sua autorização, usamos cookies próprios e da Google para saber que anúncios do Google Ads nos trazem pedidos de visita. Se os recusar, o site funciona na mesma. Pode alterar a sua escolha quando quiser em «${AJUSTES_COOKIES.pt}», no rodapé da página.`,
    mas: "Configurar e mais informação",
    menos: "Ocultar detalhes",
    detalles: [
      "Responsável: Noguera y Valdés SLU (Gándara International School), info@gandaraschool.com.",
      "Publicidade, só se aceitar: o Google Ads (Google Ireland Ltd.) guarda cookies _gcl_ (como _gcl_au e _gcl_aw) neste domínio durante 90 dias, e outros em domínios da Google, para associar o clique num anúncio ao pedido de visita. Não são usados para lhe mostrar publicidade personalizada. A Google pode tratar dados nos EUA ao abrigo do Quadro de Privacidade de Dados UE-EUA.",
      `Técnico, sempre: a sua escolha fica guardada neste navegador durante ${VIGENCIA_MESES} meses para não voltarmos a perguntar.`,
      "Pode exercer os seus direitos sobre os seus dados escrevendo para info@gandaraschool.com e reclamar junto da Agência Espanhola de Proteção de Dados (aepd.es).",
    ],
    google: "Como a Google usa estes dados",
    politica: "Política de privacidade",
    rechazar: "Recusar",
    aceptar: "Aceitar",
    cerrar: "Fechar",
    actual: { aceptadas: "Neste momento estão aceites.", rechazadas: "Neste momento estão recusados." },
  },
  fr: {
    titulo: "Cookies",
    texto: `Avec votre accord, nous utilisons nos propres cookies et ceux de Google pour savoir quelles annonces Google Ads nous amènent des demandes de visite. Si vous les refusez, le site fonctionne tout aussi bien. Vous pouvez modifier votre choix à tout moment via « ${AJUSTES_COOKIES.fr} », en bas de page.`,
    mas: "Paramétrer et en savoir plus",
    menos: "Masquer les détails",
    detalles: [
      "Responsable : Noguera y Valdés SLU (Gándara International School), info@gandaraschool.com.",
      "Publicité, seulement si vous acceptez : Google Ads (Google Ireland Ltd.) dépose des cookies _gcl_ (comme _gcl_au et _gcl_aw) sur ce domaine pendant 90 jours, et d'autres sur des domaines Google, pour relier le clic sur une annonce à la demande de visite. Ils ne servent pas à vous montrer de la publicité personnalisée. Google peut traiter des données aux États-Unis dans le cadre du Data Privacy Framework UE-États-Unis.",
      `Technique, toujours : votre choix est conservé dans ce navigateur pendant ${VIGENCIA_MESES} mois pour ne pas vous le redemander.`,
      "Vous pouvez exercer vos droits sur vos données en écrivant à info@gandaraschool.com et introduire une réclamation auprès de l'Agence espagnole de protection des données (aepd.es).",
    ],
    google: "Comment Google utilise ces données",
    politica: "Politique de confidentialité",
    rechazar: "Refuser",
    aceptar: "Accepter",
    cerrar: "Fermer",
    actual: { aceptadas: "Vous les acceptez actuellement.", rechazadas: "Vous les refusez actuellement." },
  },
};

/**
 * Banner de cookies. Sale hasta que la persona acepta o rechaza (los dos botones iguales, como pide
 * la AEPD) y se vuelve a abrir desde «Configurar cookies» para cambiar de opinión. Con una sola
 * finalidad opcional (Google Ads), configurar es aceptar o rechazar: el desplegable es la segunda capa.
 */
export const CookieBanner = () => {
  const { language } = useLanguage();
  const [visible, setVisible] = useState(() => leerConsentimiento() === null);
  const [detalles, setDetalles] = useState(false);
  const titulo = useRef<HTMLHeadingElement>(null);
  // Al reabrir desde el pie, el foco vuelve a ese enlace al cerrar (teclado y lectores de pantalla)
  const volverA = useRef<HTMLElement | null>(null);

  useEffect(
    () =>
      alAbrirAjustesCookies(() => {
        if (document.activeElement instanceof HTMLElement) volverA.current = document.activeElement;
        setVisible(true);
        requestAnimationFrame(() => titulo.current?.focus());
      }),
    [],
  );
  // Si se eligió en otra pestaña, esta deja de preguntar (y vuelve a hacerlo si la elección caducó)
  useEffect(() => alCambiarConsentimiento(() => setVisible(leerConsentimiento() === null)), []);

  if (!visible) return null;
  const t = TEXTOS_COOKIES[language] || TEXTOS_COOKIES.es;
  const actual = leerConsentimiento();
  const cerrar = () => {
    setVisible(false);
    setDetalles(false);
    volverA.current?.focus();
    volverA.current = null;
  };
  const elegir = (eleccion: Eleccion) => {
    guardarConsentimiento(eleccion);
    cerrar();
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookies-titulo"
      aria-describedby="cookies-texto"
      onKeyDown={(e) => {
        // Escape solo cierra si ya hay una elección: la primera vez hay que aceptar o rechazar
        if (e.key === "Escape" && actual) cerrar();
      }}
      className="fixed inset-x-0 bottom-0 z-50 p-3 sm:bottom-5 sm:left-5 sm:right-auto sm:max-w-md sm:p-0 print:hidden"
    >
      <div className="relative max-h-[calc(100dvh-1.5rem)] overflow-y-auto rounded-lg border border-border bg-card p-4 text-card-foreground shadow-lg sm:max-h-[calc(100dvh-2.5rem)] sm:p-5">
        {actual && (
          <button
            type="button"
            onClick={cerrar}
            aria-label={t.cerrar}
            title={t.cerrar}
            className="absolute right-1.5 top-1.5 inline-flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <h2 id="cookies-titulo" ref={titulo} tabIndex={-1} className="text-lg font-medium focus:outline-none">
          {t.titulo}
        </h2>
        <p id="cookies-texto" className="mt-2 text-sm leading-snug sm:leading-relaxed">
          {t.texto}
        </p>
        {actual && <p className="mt-2 text-sm font-medium">{t.actual[actual]}</p>}
        <button
          type="button"
          onClick={() => setDetalles(!detalles)}
          aria-expanded={detalles}
          aria-controls="cookies-detalles"
          className="mt-2 text-sm font-medium text-primary underline underline-offset-4 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {detalles ? t.menos : t.mas}
        </button>
        {detalles && (
          <div id="cookies-detalles" className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground">
            {t.detalles.map((parrafo) => (
              <p key={parrafo}>{parrafo}</p>
            ))}
            <p>
              <a
                href={`https://policies.google.com/technologies/partner-sites?hl=${language}`}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4 hover:text-foreground"
              >
                {t.google}
              </a>
              {" · "}
              <Link to={rutaPrivacidad(language)} className="underline underline-offset-4 hover:text-foreground">
                {t.politica}
              </Link>
            </p>
          </div>
        )}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Button type="button" onClick={() => elegir("rechazadas")}>
            {t.rechazar}
          </Button>
          <Button type="button" onClick={() => elegir("aceptadas")}>
            {t.aceptar}
          </Button>
        </div>
      </div>
    </div>
  );
};

/** «Configurar cookies»: para el pie de las páginas */
export const CookieSettingsLink = ({ className = "" }: { className?: string }) => {
  const { language } = useLanguage();
  return (
    <button type="button" onClick={abrirAjustesCookies} className={`underline underline-offset-4 ${className}`}>
      {AJUSTES_COOKIES[language] || AJUSTES_COOKIES.es}
    </button>
  );
};
